import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ImageIcon, ArrowRight, Layers, Sparkles } from 'lucide-react';
import { fetchGallery } from '@/services/api';

interface GalleryImage {
    id: number;
    title: string;
    image: string;
    slug: string;
    thumbnail: string;
    images_count: number;
}

const GalleryPreview = () => {
    const [images, setImages] = useState<GalleryImage[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchHomeGallery = async () => {
            try {
                const response = await fetchGallery();
                const data = response.data;
                
                const galleryItems = data.slice(0, 6).map((item: any) => ({
                    id: item.id,
                    title: item.title,
                    images_count: item.images_count || 0,
                    thumbnail: item.thumbnail || 'https://res.cloudinary.com/dwrqyrm84/image/upload/v1747853348/ulqyzwzdazyrxpderkzr.jpg',
                }));

                setImages(galleryItems);
            } catch (error) {
                console.error('Failed to fetch gallery:', error);
                setImages([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHomeGallery();
    }, []);

    return (
        <section className="py-32 bg-white relative overflow-hidden">
            {/* Background Architectural Watermark */}
            <div className="absolute top-20 left-[-2%] text-[15rem] font-serif font-black text-slate-50/50 select-none leading-none z-0 pointer-events-none">
                Archives
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
                    <div className="max-w-2xl">
                        <motion.div 
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="flex items-center gap-3 text-amber-600 mb-6"
                        >
                            <Sparkles size={16} className="animate-pulse" />
                            <span className="text-[10px] font-black uppercase tracking-[0.4em]">Diocesan Visual Storytelling</span>
                        </motion.div>
                        <h2 className="text-5xl md:text-7xl font-serif font-bold text-slate-900 leading-[1.1]">
                            Diocesan Life <br/>
                            <span className="text-amber-600 italic font-medium">in Frames</span>
                        </h2>
                    </div>
                    
                    <Link to="/gallery">
                        <motion.button 
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="group flex items-center gap-4 bg-slate-900 text-white px-8 py-4 rounded-full text-[10px] font-black uppercase tracking-widest transition-all hover:bg-amber-600 hover:shadow-xl hover:shadow-amber-900/20"
                        >
                            Explore Library <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                    </Link>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="aspect-[4/5] bg-slate-100 animate-pulse rounded-[2.5rem]" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                        {images.map((image, index) => (
                            <motion.div
                                key={image.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1, duration: 0.8 }}
                            >
                                <Link to={`/gallery/${image.id}`} className="group relative block">
                                    <div className="relative aspect-[4/5] overflow-hidden rounded-[3rem] shadow-2xl shadow-slate-200 bg-slate-100 border border-slate-100">
                                        
                                        {/* Image with subtle desaturation */}
                                        <img
                                            src={image.thumbnail}
                                            alt={image.title}
                                            className="w-full h-full object-cover transition-all duration-[1.5s] ease-out group-hover:scale-110 grayscale-[40%] group-hover:grayscale-0"
                                        />

                                        {/* Cinematic Gradient Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />

                                        {/* Inner Frame - The "Retouch" Effect */}
                                        <div className="absolute inset-6 border border-white/10 rounded-[2rem] pointer-events-none group-hover:inset-8 group-hover:border-amber-500/30 transition-all duration-700 ease-in-out" />

                                        {/* Content Information */}
                                        <div className="absolute inset-0 flex flex-col justify-end p-10">
                                            <div className="overflow-hidden">
                                                <motion.div 
                                                    initial={{ y: 20, opacity: 0 }}
                                                    whileInView={{ y: 0, opacity: 1 }}
                                                    className="flex items-center gap-2 text-amber-500 mb-3"
                                                >
                                                    <Layers size={12} />
                                                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/80">
                                                        {image.images_count} Captures
                                                    </span>
                                                </motion.div>
                                                <h3 className="text-2xl font-serif font-bold text-white leading-tight group-hover:text-amber-400 transition-colors duration-300">
                                                    {image.title}
                                                </h3>
                                            </div>
                                        </div>

                                        {/* Hover Icon Reveal */}
                                        <div className="absolute top-8 right-8 bg-white/10 backdrop-blur-md p-3 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-[-10px] group-hover:translate-y-0">
                                            <ImageIcon size={18} className="text-white" />
                                        </div>
                                    </div>
                                    
                                    {/* Mobile helper text */}
                                    <div className="mt-6 flex items-center justify-between px-4 lg:hidden">
                                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-tighter">View Album</span>
                                        <ArrowRight size={14} className="text-amber-600" />
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Bottom Architectural Line */}
                <div className="mt-24 flex items-center gap-8">
                    <div className="h-px flex-1 bg-slate-100" />
                    <div className="flex gap-2">
                        {[1, 2, 3].map(dot => (
                            <div key={dot} className="h-1.5 w-1.5 rounded-full bg-amber-500/20" />
                        ))}
                    </div>
                    <div className="h-px flex-1 bg-slate-100" />
                </div>
            </div>
        </section>
    );
};

export default GalleryPreview;